import { NEW_GEOFENCE_NAME, GEOFENCE_NAME_MAX_LENGTH, PROPORTIONATE_RADIUS_SIZES, DEFAULT_GEOFENCE_TYPE } from './constants';
import circle from '@turf/circle';
import bbox from '@turf/bbox';
import bboxPolygon from '@turf/bbox-polygon';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';


const getGeofenceProperty = (state, id, propertyName) => {
    const geofence = id ? state.data.geofences.lookup[id] : state.data.geofences.new;
    return (geofence && geofence[propertyName]) || {};
};

const geofenceAddress = (state, id) => {
    const geojson = state.data.geofences.editingGeometry || getGeofenceProperty(state, id, 'geojson').value;
    return geojson && geojson.properties && geojson.properties.address;
};

const geofenceNameFromAddress = (state, id) => {
    const address = geofenceAddress(state, id);
    return address
        && address.split(',')[0].substring(0, GEOFENCE_NAME_MAX_LENGTH);
}

const getPointGeometryCircleCoordinates = (geofence) => {
    const circleGeometry = geofence.geometry;

    const circleCenter = circleGeometry.coordinates;
    const circleRadius = geofence.properties.radius / 1000; // in meters

    // create a polygon that represents the circle (not exactly a circle but very close to it)
    const circlePolygon = circle(circleCenter, circleRadius);

    // calculate the bounding box of the circle polygon
    const circlePolygonBoundingBox = bbox(circlePolygon.geometry);
    const boundingBoxRectangle = bboxPolygon(circlePolygonBoundingBox);

    return boundingBoxRectangle.geometry.coordinates;
}

export default {

    loading: (state) => state.data.geofences.loading,

    activeGeofenceIds: (state) => state.data.geofences.activeGeofenceIds,

    wrappingGeofences: (state, coordinates) => {
        const list = [];

        state.data.geofences.activeGeofenceIds.map(id => {
            const g = state.data.geofences.lookup[id];

            const type = g.geojson.value.geometry.type;

            let isInside;

            if(type === "Point"){
                isInside = booleanPointInPolygon(coordinates, {coordinates: getPointGeometryCircleCoordinates(g.geojson.value), type: "Polygon"});
            }
            else{
                isInside = booleanPointInPolygon(coordinates, g.geojson.value.geometry);
            }

            if(isInside){
                list.push({
                    name: g.name.value,
                    type,
                    geometry: g.geojson.value.geometry,
                    color: g.style.value['fill-color'],
                });
            }
        });

        return list;
    },

    isGeofenceVisible: (state, id) => state.data.geofences.lookup[id].visible,

    geofenceName: (state, id) => getGeofenceProperty(state, id, 'name').value || geofenceNameFromAddress(state, id) || NEW_GEOFENCE_NAME,
    geofenceNameChangePending: (state, id) => getGeofenceProperty(state, id, 'name').pending,

    geofenceType: (state, id, firstCheckEditingGeometry = false) => {
        let geojson;
        if(firstCheckEditingGeometry)
            geojson = state.data.geofences.editingGeometry || getGeofenceProperty(state, id, 'geojson').value;
        else{
            geojson = getGeofenceProperty(state, id, 'geojson').value;
        }

        let geofenceType = geojson && geojson.geometry && geojson.geometry.type;
        
        if(!geofenceType) geofenceType = DEFAULT_GEOFENCE_TYPE;
        return geofenceType;
    },

    geofenceValidity: (state, id) => {
        if(state.data.geofences.editingGeometry && state.data.geofences.editingGeometry.geometry.type === "Polygon")  return {isValid: state.data.geofences.editingGeometry.isValid, error: state.data.geofences.editingGeometry.error};
        return {isValid: true, error: null};
    },

    geofenceIsImported: (state) => {
        return state.data.geofences.editingGeometry.imported; // Only for currently editing geofence we check if it's imported
    },

    geofenceExpiryDate: (state, id) => getGeofenceProperty(state, id, 'end').value,
    geofenceExpiryDatePending: (state, id) => getGeofenceProperty(state, id, 'end').pending,

    geofenceGeojson: (state, id) => {
        if(id && id !== "imported") return getGeofenceProperty(state, id, 'geojson').value;
        
       return state.data.geofences.editingGeometry;
    },
    geofenceGeojsonChangePending: (state, id) => getGeofenceProperty(state, id, 'geojson').pending,

    geofenceNotification: (state, id) => getGeofenceProperty(state, id, 'notification').value,
    geofenceNotificationChangePending: (state, id) => getGeofenceProperty(state, id, 'notification').pending,

    geofenceStyle: (state, id) => getGeofenceProperty(state, id, 'style').value,
    geofenceColor: (state, id) => {
        const style = getGeofenceProperty(state, id, 'style').value;
        return style && style['fill-color'];
    },
    geofenceStyleChangePending: (state, id) => id ? getGeofenceProperty(state, id, 'style').pending : false,

    geofenceRadiusInKm: (state, id) => {
        const geojson =  state.data.geofences.editingGeometry || getGeofenceProperty(state, id, 'geojson').value;
        return geojson && geojson.properties && geojson.properties.radius/1000;
    },

    geofenceAreaInKm2: (state, id) => {
        const geojson =  state.data.geofences.editingGeometry || getGeofenceProperty(state, id, 'geojson').value;
        return geojson && geojson.properties && geojson.properties.area / 1000000;
    },

    geofenceCoordinates: (state, id) => {
        const geojson = state.data.geofences.editingGeometry || getGeofenceProperty(state, id, 'geojson').value;
        return geojson && geojson.geometry && geojson.geometry.coordinates;
    },

    geofenceAddress,

    editingGeometry: (state) => state.data.geofences.editingGeometry,
    savingGeometryEdit: (state, id) => {
        if (state.data.geofences.editingGeometry) {
            return state.data.geofences.editingGeometry.pending;
        }
        return getGeofenceProperty(state, id, 'geojson').pending;
    },

    newGeofence: (state) => state.data.geofences.new,

    geofenceProportionateSize: (state, id) => {
        const { BIG, MEDIUM, SMALL } = PROPORTIONATE_RADIUS_SIZES;
        const geojson = getGeofenceProperty(state, id, 'geojson').value;

        if(!geojson || !geojson.properties) return BIG;
        
        if(geojson.geometry.type === 'Polygon'){
            const areaInKm2 = geojson.properties.area / 1000000;
            
            if (!areaInKm2) return BIG;

            if (areaInKm2 >= 3.14) {
                return BIG;
            } else if (areaInKm2 >= 0.78) {
                return MEDIUM;
            }
            return SMALL;
        }

        // it's a circle
        const radiusInKm = geojson.properties.radius / 1000;

        if (!radiusInKm) return BIG;

        if (radiusInKm >= 1) {
            return BIG;
        } else if (radiusInKm >= 0.5) {
            return MEDIUM;
        }
        return SMALL;

        
    }
};
